import { useNavigate } from 'react-router-dom';
import { Check, TrendingUp, ArrowLeft, Gift, Zap, Brain, BookOpen, BarChart2, Shield, Star, MessageSquare, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/utils/i18n';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useAuth } from '@/context/AuthContext';

const ALL_FEATURES = {
  en: [
    { icon: '📰', label: 'Unlimited financial news feed' },
    { icon: '💼', label: 'Unlimited portfolio tracker' },
    { icon: '💎', label: 'AI Gems detector (stocks & crypto)' },
    { icon: '🎓', label: 'Full Academy — 5 complete modules' },
    { icon: '📊', label: 'AI Chart Analyzer' },
    { icon: '🤖', label: 'AI Investment Chat 24/7' },
    { icon: '🏦', label: 'Professional Analysis (Goldman, BlackRock, McKinsey)' },
    { icon: '🎮', label: 'Trading Simulator ($10k virtual)' },
    { icon: '📈', label: 'AI Recommendations (GPT-5, Claude, Gemini)' },
    { icon: '⚡', label: 'Real-time Market Sentiment AI' },
  ],
  fr: [
    { icon: '📰', label: 'Actualités financières illimitées' },
    { icon: '💼', label: 'Portfolio illimité' },
    { icon: '💎', label: 'Détecteur de Pépites IA (actions & crypto)' },
    { icon: '🎓', label: 'Académie complète — 5 modules' },
    { icon: '📊', label: 'Analyseur de Graphiques IA' },
    { icon: '🤖', label: 'Chat Investissement IA 24/7' },
    { icon: '🏦', label: 'Analyses Pro (Goldman, BlackRock, McKinsey)' },
    { icon: '🎮', label: 'Simulateur de Trading (10k$ virtuels)' },
    { icon: '📈', label: 'Recommandations IA (GPT-5, Claude, Gemini)' },
    { icon: '⚡', label: 'Sentiment Marché IA en temps réel' },
  ],
  es: [
    { icon: '📰', label: 'Noticias financieras ilimitadas' },
    { icon: '💼', label: 'Cartera ilimitada' },
    { icon: '💎', label: 'Detector de Gemas IA (acciones y cripto)' },
    { icon: '🎓', label: 'Academia completa — 5 módulos' },
    { icon: '📊', label: 'Analizador de Gráficos IA' },
    { icon: '🤖', label: 'Chat Inversión IA 24/7' },
    { icon: '🏦', label: 'Análisis Pro (Goldman, BlackRock, McKinsey)' },
    { icon: '🎮', label: 'Simulador de Trading ($10k virtual)' },
    { icon: '📈', label: 'Recomendaciones IA (GPT-5, Claude, Gemini)' },
    { icon: '⚡', label: 'Sentimiento Mercado IA en tiempo real' },
  ],
};

const FREE_FEATURES = {
  en: ['News (5 articles/day)', 'Portfolio (3 assets)', 'Academy — Module 1', 'Trading Simulator'],
  fr: ['Actualités (5 articles/jour)', 'Portfolio (3 actifs)', 'Académie — Module 1', 'Simulateur de Trading'],
  es: ['Noticias (5 artículos/día)', 'Cartera (3 activos)', 'Academia — Módulo 1', 'Simulador de Trading'],
};

function Pricing() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const { user } = useAuth();

  const features = ALL_FEATURES[language] || ALL_FEATURES.en;
  const freeFeatures = FREE_FEATURES[language] || FREE_FEATURES.en;

  const labels = {
    en: {
      title: 'One plan. Everything included.',
      subtitle: 'No confusing tiers. No feature limits. Just full access.',
      fullAccess: 'Full Access',
      perMonth: '/month',
      getStarted: 'Get started for €19/month',
      noCard: 'No long-term commitment • Cancel anytime',
      everythingIn: 'Everything included:',
      freePlan: 'Or start for free',
      freeDesc: 'Try the basics, no credit card required.',
      freeIncludes: "Free plan includes:",
      startFree: 'Start for free',
      upgrade: 'Upgrade to Full Access',
      notSure: 'Why one price?',
      notSureDesc: 'Simple pricing means you get everything without wondering if you\'re missing features. Just like Spotify — one price, all the music.',
    },
    fr: {
      title: 'Un seul plan. Tout inclus.',
      subtitle: 'Pas de niveaux confus. Pas de limites. Juste un accès complet.',
      fullAccess: 'Accès Complet',
      perMonth: '/mois',
      getStarted: 'Commencer pour €19/mois',
      noCard: 'Sans engagement • Annulez à tout moment',
      everythingIn: 'Tout est inclus :',
      freePlan: 'Ou commencer gratuitement',
      freeDesc: 'Testez les fonctionnalités de base, sans carte bancaire.',
      freeIncludes: 'Plan gratuit inclut :',
      startFree: 'Commencer gratuitement',
      upgrade: 'Passer à l\'Accès Complet',
      notSure: 'Pourquoi un seul prix ?',
      notSureDesc: 'Un prix simple = vous avez tout sans vous demander si vous ratez des fonctionnalités. Comme Spotify — un prix, toute la musique.',
    },
    es: {
      title: 'Un plan. Todo incluido.',
      subtitle: 'Sin niveles confusos. Sin límites. Solo acceso completo.',
      fullAccess: 'Acceso Completo',
      perMonth: '/mes',
      getStarted: 'Empezar por €19/mes',
      noCard: 'Sin compromiso • Cancela cuando quieras',
      everythingIn: 'Todo incluido:',
      freePlan: 'O empieza gratis',
      freeDesc: 'Prueba las funciones básicas, sin tarjeta de crédito.',
      freeIncludes: 'Plan gratuito incluye:',
      startFree: 'Empezar gratis',
      upgrade: 'Pasar al Acceso Completo',
      notSure: '¿Por qué un solo precio?',
      notSureDesc: 'Un precio simple = tienes todo sin preguntarte si te falta algo. Como Spotify — un precio, toda la música.',
    },
  };
  const lbl = labels[language] || labels.en;

  const handleGetStarted = () => {
    if (!user) {
      navigate('/?mode=register&plan=premium');
    } else {
      navigate('/checkout?plan=premium');
    }
  };

  const handleStartFree = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/?mode=register');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a0a0a] to-[#0a0a0a]">
      {/* Header */}
      <header className="border-b border-orange-900/20 backdrop-blur-sm bg-black/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button data-testid="back-to-home" onClick={() => navigate('/')} variant="ghost" size="sm" className="text-gray-400 hover:text-orange-500">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/logo.png" alt="DO NOT BULL" className="w-10 h-10" />
              <div>
                <div className="text-xl font-bold text-white tracking-tight">DO NOT BULL</div>
                <div className="text-[10px] text-orange-500 font-semibold tracking-widest">ANTI-SPECULATION AI v2.0</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Button onClick={handleGetStarted} className="bg-orange-500 hover:bg-orange-600 text-black font-bold">
              {lbl.getStarted}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-400 text-sm font-bold uppercase tracking-widest mb-6">
            <Star className="w-4 h-4" />
            {lbl.fullAccess}
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4 leading-tight">{lbl.title}</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">{lbl.subtitle}</p>
        </div>

        {/* Main Plan Card */}
        <div data-testid="pricing-plan-premium" className="relative bg-gradient-to-br from-zinc-900 to-black rounded-3xl border-2 border-orange-500 shadow-2xl shadow-orange-500/20 overflow-hidden mb-8">
          {/* Top banner */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 py-3 text-center">
            <span className="text-black font-black text-sm tracking-widest uppercase">
              {language === 'fr' ? '✦ TOUT INCLUS ✦' : language === 'es' ? '✦ TODO INCLUIDO ✦' : '✦ EVERYTHING INCLUDED ✦'}
            </span>
          </div>

          <div className="p-8 sm:p-12">
            {/* Price */}
            <div className="text-center mb-10">
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-7xl font-black text-white">€19</span>
                <span className="text-2xl text-gray-400">{lbl.perMonth}</span>
              </div>
              <p className="text-gray-500 mt-2 text-sm">{lbl.noCard}</p>
            </div>

            {/* CTA Button */}
            <Button
              data-testid="get-full-access-btn"
              onClick={handleGetStarted}
              className="w-full py-6 text-xl font-black rounded-2xl bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-black mb-10"
            >
              {lbl.getStarted} →
            </Button>

            {/* Features */}
            <div>
              <p className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-6">{lbl.everythingIn}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {features.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-[#0a0a0a]/60 rounded-xl">
                    <span className="text-xl flex-shrink-0">{f.icon}</span>
                    <span className="text-gray-300 text-sm font-medium">{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Free plan */}
        <div className="relative bg-gradient-to-br from-zinc-900 to-black rounded-2xl border border-green-500/30 p-8 mb-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <Gift className="w-5 h-5 text-green-400" />
                <h3 className="text-lg font-bold text-white">{lbl.freePlan}</h3>
              </div>
              <p className="text-gray-400 text-sm mb-4">{lbl.freeDesc}</p>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">{lbl.freeIncludes}</p>
              <div className="grid grid-cols-2 gap-1.5">
                {freeFeatures.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-400">
                    <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="text-center">
                <div className="text-4xl font-black text-green-400">$0</div>
                <div className="text-gray-500 text-sm">{language === 'fr' ? 'pour toujours' : language === 'es' ? 'para siempre' : 'forever'}</div>
              </div>
              <Button onClick={handleStartFree} className="bg-green-500 hover:bg-green-600 text-black font-bold px-6 whitespace-nowrap">
                {lbl.startFree} →
              </Button>
            </div>
          </div>
        </div>

        {/* Why one price */}
        <div className="bg-gradient-to-br from-orange-500/5 to-red-500/5 rounded-2xl border border-orange-500/20 p-8 text-center">
          <TrendingUp className="w-10 h-10 text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-3">{lbl.notSure}</h2>
          <p className="text-gray-400 max-w-lg mx-auto text-sm leading-relaxed">{lbl.notSureDesc}</p>
        </div>
      </div>
    </div>
  );
}

export default Pricing;
